import React from "react";
import {
  Form,
  Row,
  Col,
  InputGroup,
  Button,
  Spinner,
} from "react-bootstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AddTrial.css";
import {
  RELATIONSHIP_OPTIONS,
  type AcademyOption,
  type AgeCategoryOption,
  type CenterOption,
  type CourseOption,
  type SlotOption,
  type SportOption,
  type TrialFormState,
  type TrialFormLoading,
  type RelationshipOption,
} from "./useTrialForm";
import { PermissionWrapper } from "../../../components/PermissionWrapper/PermissionWrapper";
import { TRIALS_MANAGE_PERMS } from "../permissions";

interface TrialFormProps {
  form: TrialFormState;
  loading: TrialFormLoading;
  errors: Partial<Record<keyof TrialFormState, string>>;
  sportOptions: SportOption[];
  academyOptions: AcademyOption[];
  centerOptions: CenterOption[];
  courseOptions: CourseOption[];
  ageCategoryOptions: AgeCategoryOption[];
  availableDates: string[];
  slotOptions: SlotOption[];
  isFormValid: () => boolean;
  setField: <K extends keyof TrialFormState>(
    field: K,
    value: TrialFormState[K]
  ) => void;
  onSubmit: () => void;
}

const TrialForm: React.FC<TrialFormProps> = ({
  form,
  loading,
  errors,
  sportOptions,
  academyOptions,
  centerOptions,
  courseOptions,
  ageCategoryOptions,
  availableDates,
  slotOptions,
  isFormValid,
  setField,
  onSubmit,
}) => {
  // Build a Set of enabled date strings for O(1) lookup in filterDate
  const enabledDatesSet = React.useMemo(
    () => new Set(availableDates),
    [availableDates]
  );

  const filterDate = React.useCallback(
    (date: Date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return enabledDatesSet.has(`${y}-${m}-${d}`);
    },
    [enabledDatesSet]
  );

  return (
    <div>
      {/* ── Section 1: Player Details ── */}
      <div className="enrolment-form-section">
        <h6 className="enrolment-section-title">
          <span className="enrolment-section-number">1</span>
          Player Details
        </h6>

        <Row className="g-3">
          {/* Player Name */}
          <Col xs={12} md={6}>
            <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
              Player Name *
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter player's full name"
              value={form.playerName}
              onChange={(e) => setField("playerName", e.target.value)}
              isInvalid={!!errors.playerName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.playerName}
            </Form.Control.Feedback>
          </Col>

          {/* Mobile Number */}
          <Col xs={12} md={6}>
            <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
              Mobile Number *
            </Form.Label>
            <InputGroup>
              <InputGroup.Text className="trial-phone-prefix">+91</InputGroup.Text>
              <Form.Control
                type="tel"
                placeholder="Enter 10-digit mobile number"
                value={form.mobile}
                onChange={(e) =>
                  setField("mobile", e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                maxLength={10}
                isInvalid={!!errors.mobile}
              />
              <Form.Control.Feedback type="invalid">
                {errors.mobile}
              </Form.Control.Feedback>
            </InputGroup>
          </Col>

          {/* Booked By */}
          <Col xs={12}>
            <span className="trial-booked-by-label">Booked By *</span>
            <div className="trial-radio-group">
              <Form.Check
                type="radio"
                id="enrolled-by-self"
                label="Self"
                checked={form.enrolledBy === "self"}
                onChange={() => setField("enrolledBy", "self")}
              />
              <Form.Check
                type="radio"
                id="enrolled-by-parent"
                label="Parent"
                checked={form.enrolledBy === "parent"}
                onChange={() => setField("enrolledBy", "parent")}
              />
            </div>
          </Col>

          {/* Conditional parent fields */}
          {form.enrolledBy === "parent" && (
            <>
              <Col xs={12} md={6}>
                <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
                  Parent Name *
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter parent's full name"
                  value={form.parentName}
                  onChange={(e) => setField("parentName", e.target.value)}
                  isInvalid={!!errors.parentName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.parentName}
                </Form.Control.Feedback>
              </Col>

              <Col xs={12} md={6}>
                <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
                  Relationship *
                </Form.Label>
                <Select<RelationshipOption>
                  classNamePrefix="react-select"
                  options={RELATIONSHIP_OPTIONS}
                  value={form.relationship}
                  onChange={(opt) => setField("relationship", opt)}
                  placeholder="Select relationship..."
                />
                {errors.relationship && (
                  <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                    {errors.relationship}
                  </div>
                )}
              </Col>
            </>
          )}
        </Row>
      </div>

      {/* ── Section 2: Trial Details ── */}
      <div className="enrolment-form-section">
        <h6 className="enrolment-section-title">
          <span className="enrolment-section-number">2</span>
          Trial Details
        </h6>

        <Row className="g-3">
          {/* Sport */}
          <Col xs={12} md={6}>
            <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
              Sport *
            </Form.Label>
            <Select<SportOption>
              classNamePrefix="react-select"
              options={sportOptions}
              value={form.sport}
              onChange={(opt) => setField("sport", opt)}
              placeholder="Select sport..."
            />
            {errors.sport && (
              <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                {errors.sport}
              </div>
            )}
          </Col>

          {/* Academy */}
          <Col xs={12} md={6}>
            <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
              Academy *
            </Form.Label>
            <Select<AcademyOption>
              classNamePrefix="react-select"
              options={academyOptions}
              value={form.academy}
              onChange={(opt) => setField("academy", opt)}
              isDisabled={!form.sport || loading.academies}
              isLoading={loading.academies}
              placeholder={
                !form.sport
                  ? "Select a sport first..."
                  : loading.academies
                  ? "Loading academies..."
                  : "Select academy..."
              }
            />
            {errors.academy && (
              <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                {errors.academy}
              </div>
            )}
          </Col>

          {/* Center */}
          <Col xs={12} md={6}>
            <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
              Center *
            </Form.Label>
            <Select<CenterOption>
              classNamePrefix="react-select"
              options={centerOptions}
              value={form.center}
              onChange={(opt) => setField("center", opt)}
              isDisabled={!form.academy || loading.centers}
              isLoading={loading.centers}
              placeholder={
                !form.academy
                  ? "Select an academy first..."
                  : loading.centers
                  ? "Loading centers..."
                  : "Select center..."
              }
            />
            {errors.center && (
              <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                {errors.center}
              </div>
            )}
          </Col>

          {/* Course */}
          <Col xs={12} md={6}>
            <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
              Course *
            </Form.Label>
            <Select<CourseOption>
              classNamePrefix="react-select"
              options={courseOptions}
              value={form.course}
              onChange={(opt) => setField("course", opt)}
              isDisabled={!form.center || loading.courses}
              isLoading={loading.courses}
              placeholder={
                !form.center
                  ? "Select a center first..."
                  : loading.courses
                  ? "Loading courses..."
                  : "Select course..."
              }
            />
            {errors.course && (
              <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                {errors.course}
              </div>
            )}
          </Col>

          {/* Age Category */}
          <Col xs={12} md={6}>
            <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
              Age Category *
            </Form.Label>
            <Select<AgeCategoryOption>
              classNamePrefix="react-select"
              options={ageCategoryOptions}
              value={form.ageCategory}
              onChange={(opt) => setField("ageCategory", opt)}
              isDisabled={!form.course || loading.ageCategories}
              isLoading={loading.ageCategories}
              placeholder={
                !form.course
                  ? "Select a course first..."
                  : loading.ageCategories
                  ? "Loading age categories..."
                  : "Select age category..."
              }
            />
            {errors.ageCategory && (
              <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                {errors.ageCategory}
              </div>
            )}
          </Col>

          {/* Trial Date */}
          <Col xs={12} md={6}>
            <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
              Trial Date *
            </Form.Label>
            <div
              className={`trial-date-picker-wrapper${errors.date ? " is-invalid" : ""}`}
            >
              <DatePicker
                selected={form.date}
                onChange={(date) => setField("date", date)}
                filterDate={filterDate}
                disabled={!form.ageCategory || loading.dates}
                placeholderText={
                  !form.ageCategory
                    ? "Select an age category first..."
                    : loading.dates
                    ? "Loading dates..."
                    : "Select trial date..."
                }
                dateFormat="dd MMM yyyy"
                minDate={new Date()}
                autoComplete="off"
              />
            </div>
            {loading.dates && (
              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                <Spinner size="sm" animation="border" className="me-1" />
                Loading available dates...
              </div>
            )}
            {!loading.dates && form.ageCategory && availableDates.length === 0 && (
              <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                No trial dates available for the selected age category.
              </div>
            )}
            {errors.date && (
              <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                {errors.date}
              </div>
            )}
          </Col>

          {/* Time Slot */}
          <Col xs={12} md={6}>
            <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
              Time Slot *
            </Form.Label>
            <Select<SlotOption>
              classNamePrefix="react-select"
              options={slotOptions}
              value={form.slot}
              onChange={(opt) => setField("slot", opt)}
              isDisabled={!form.date || loading.slots}
              isLoading={loading.slots}
              placeholder={
                !form.date
                  ? "Select a date first..."
                  : loading.slots
                  ? "Loading slots..."
                  : slotOptions.length === 0
                  ? "No slots available"
                  : "Select time slot..."
              }
            />
            {!loading.slots && form.date && slotOptions.length === 0 && (
              <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                No time slots available for the selected date.
              </div>
            )}
            {errors.slot && (
              <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                {errors.slot}
              </div>
            )}
          </Col>
        </Row>
      </div>

      {/* ── Submit ── */}
      <div className="d-flex justify-content-end">
        <PermissionWrapper anyOfPermissions={TRIALS_MANAGE_PERMS} hideWhenDisabled>
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={!isFormValid() || loading.submitting}
            style={{ minWidth: 140 }}
          >
            {loading.submitting ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Adding Trial...
              </>
            ) : (
              "Add Trial"
            )}
          </Button>
        </PermissionWrapper>
      </div>
    </div>
  );
};

export default TrialForm;
